/**
 * Priority + overflow math for the primary top-nav (nav rework 2026-07-13).
 *
 * The top bar must NEVER push the page wider than the viewport. When the
 * gated nav items don't all fit, the lowest-priority ones (registry-order
 * tail) collapse into a "More" menu. These two PURE functions carry the
 * logic; PrimaryNav wires them to a live width measurement (ResizeObserver),
 * and the mobile-width case falls out for free — a very narrow bar fits zero
 * items, so "More" becomes the full menu the founder asked for.
 *
 * Kept pure + framework-free so the split is unit-testable without a real
 * layout engine (jsdom has none): the tests feed fixture widths and assert
 * the greedy fit + the slice, rather than simulating a browser.
 */

/**
 * How many leading items fit, given each item's rendered width, the available
 * width, and the width the "More" trigger would occupy.
 *
 * If everything fits, all items stay in the bar (no "More" needed). Otherwise
 * a "More" trigger is guaranteed to render, so its width is reserved up front
 * and items are greedily admitted until the next one would overflow. A bar too
 * narrow for even one item returns 0 — every item lives in "More".
 *
 * Widths are in px (or any consistent unit); item widths should already
 * include the inter-item gap so the running sum matches the real row.
 */
export function computeVisibleCount(
  itemWidths: readonly number[],
  available: number,
  moreWidth: number,
): number {
  const total = itemWidths.reduce((sum, w) => sum + w, 0);
  if (total <= available) return itemWidths.length;

  // A "More" trigger is now unavoidable — reserve its footprint first.
  let used = moreWidth;
  let count = 0;
  for (const w of itemWidths) {
    if (used + w > available) break;
    used += w;
    count += 1;
  }
  return count;
}

/**
 * Mobile bottom-bar slot cap (nav rework 2026-07-13). The bar shows at most
 * `maxSlots` items (iOS convention, 5); when the gated list is longer, the
 * last slot becomes "More" and holds the tail, so only `maxSlots - 1` real
 * items sit in the bar. Fixed-count (no width measurement) — a phone-narrow
 * bar has predictable room, and equal-width slots keep labels from colliding.
 */
export function computeMobileVisibleCount(total: number, maxSlots: number): number {
  if (total <= maxSlots) return total;
  return Math.max(0, maxSlots - 1);
}

/**
 * Split an ordered, ALREADY-GATED list into the leading `visibleCount` shown
 * in the bar and the remaining tail routed to the "More" menu. Splitting the
 * gated list (never the full registry) is what keeps role-hidden items hidden
 * in the overflow menu too.
 */
export function splitNavLinks<T>(
  links: readonly T[],
  visibleCount: number,
): { visible: readonly T[]; overflow: readonly T[] } {
  const n = Math.max(0, Math.min(visibleCount, links.length));
  return { visible: links.slice(0, n), overflow: links.slice(n) };
}
